import React from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import './ConfirmationDialog.css';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'danger' | 'primary' | 'secondary';
  onConfirm: () => void;
  onCancel: () => void;
  icon?: React.ReactNode;
  isLoading?: boolean;
  loadingText?: string;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
  icon,
  isLoading = false,
  loadingText = 'Processing...'
}) => {
  if (!isOpen) return null;

  const getConfirmButtonStyle = () => {
    switch (confirmVariant) {
      case 'danger':
        return {
          background: 'rgba(239, 68, 68, 0.9)',
          hoverBackground: 'rgba(220, 38, 38, 0.9)'
        };
      case 'secondary':
        return {
          background: 'rgba(107, 114, 128, 0.9)',
          hoverBackground: 'rgba(75, 85, 99, 0.9)'
        };
      default: // primary
        return {
          background: 'rgba(5, 150, 105, 0.9)',
          hoverBackground: 'rgba(4, 120, 87, 0.9)'
        };
    }
  };

  const buttonStyle = getConfirmButtonStyle();

  return ReactDOM.createPortal(
    <div 
      className="confirmation-dialog-overlay"
      onClick={onCancel}
    >
      <div 
        className="confirmation-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="confirmation-dialog-header">
          {icon && (
            <div className="confirmation-dialog-icon">
              {icon}
            </div>
          )}
          <h3 className="confirmation-dialog-title">{title}</h3>
          <button 
            className="confirmation-dialog-close"
            onClick={onCancel}
            disabled={isLoading}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Message */}
        <p className="confirmation-dialog-message">{message}</p>

        {/* Actions */}
        <div className="confirmation-dialog-actions">
          <button
            onClick={onCancel}
            className="confirmation-dialog-btn cancel"
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className="confirmation-dialog-btn confirm"
            style={{
              background: buttonStyle.background
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.background = buttonStyle.hoverBackground;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = buttonStyle.background;
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="confirmation-dialog-loading">
                <div className="confirmation-dialog-spinner"></div>
                {loadingText}
              </span>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmationDialog;
